import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Keyboard, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/components/attendance/ui";
import Stage from "./Stage";
import HymnSearch from "./HymnSearch";
import ConsoleControls from "./ConsoleControls";
import VerseRail from "./VerseRail";
import ProgrammePanel from "./ProgrammePanel";
import BiblePanel from "@/components/worship/BiblePanel";
import AnnouncementsPanel from "@/components/worship/AnnouncementsPanel";
import SongsPanel from "@/components/worship/SongsPanel";
import { presentable } from "@/components/worship/content";
import { buildSlides, CHANNEL, DEFAULT_SETTINGS, LANG_LABEL, numberLabel, protectHandlers, slideLabel } from "./projection";

const NEXT = ["ArrowRight", "ArrowDown", "PageDown", " "];
const PREV = ["ArrowLeft", "ArrowUp", "PageUp"];
const loadSettings = () => { try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem("hoh-proj-settings") || "{}") }; } catch { return DEFAULT_SETTINGS; } };

export default function Console({ meta, me }) {
  const [prefs, setPrefs] = useState({ language: "en", edition_id: "", denomination_id: null, ...me.settings });
  const [settings, setSettings] = useState(loadSettings);
  const [hymn, setHymn] = useState(null);
  const [custom, setCustom] = useState(null);
  const [loadingHymn, setLoadingHymn] = useState(false);
  const [index, setIndex] = useState(0);
  const [blank, setBlank] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [panel, setPanel] = useState("search");
  const chan = useRef(null);
  const fsRef = useRef(null);
  const state = useRef({});

  const denom = meta.denominations.find((d) => d.id === prefs.denomination_id) || meta.denominations.find((d) => d.is_default);
  const slides = useMemo(() => (custom ? custom.slides : buildSlides(hymn, settings.repeatChorus)), [custom, hymn, settings.repeatChorus]);
  const slide = slides[index];
  const stageMeta = custom ? { number: custom.label, title: "", position: `${slide?.label || ""} · ${index + 1}/${slides.length}` } : hymn && { number: numberLabel(hymn, denom), title: hymn.title, position: `${slideLabel(slide)} · ${index + 1}/${slides.length}` };
  state.current = { slides, index, hymn, blank };

  useEffect(() => { localStorage.setItem("hoh-proj-settings", JSON.stringify(settings)); }, [settings]);
  useEffect(() => protectHandlers(), []);

  const post = useCallback((msg) => { try { chan.current?.postMessage(msg); } catch { /* channel closed */ } }, []);
  const payload = useCallback(() => ({ slide, meta: stageMeta, settings, blank }), [slide, stageMeta, settings, blank]);
  useEffect(() => { post({ type: "state", payload: payload() }); }, [payload, post]);

  const go = useCallback((i) => { const n = state.current.slides.length; if (n) { setIndex(Math.max(0, Math.min(n - 1, i))); setBlank(false); } }, []);
  const step = useCallback((d) => go(state.current.index + d), [go]);

  const load = useCallback(async (id, keepIndex = false) => {
    setLoadingHymn(true);
    try {
      const r = await api.get(`/projection/hymns/${id}`, { params: { denomination_id: denom?.id, edition_id: prefs.edition_id || "" } });
      setHymn(r.data);
      setCustom(null);
      if (!keepIndex) setIndex(0);
      setBlank(false);
    } catch (e) { toast.error(errMsg(e, "Could not load hymn")); } finally { setLoadingHymn(false); }
  }, [denom?.id, prefs.edition_id]);

  const present = useCallback((c) => { setCustom(c); setIndex(0); setBlank(!!c.blank); }, []);
  const openItem = useCallback(async (it) => {
    if (it.type === "hymn" || !it.type) { load(it.hymn_id); return; }
    try { present(await presentable(it)); } catch (e) { toast.error(errMsg(e, "Could not open this item")); }
  }, [load, present]);
  useEffect(() => {
    if (!custom?.rotate || slides.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % state.current.slides.length), custom.rotate * 1000);
    return () => clearInterval(t);
  }, [custom, slides.length]);
  const currentDesc = custom ? custom.desc : hymn && { type: "hymn", hymn_id: hymn.id, label: hymn.title };
  const sibling = !custom && hymn?.siblings?.find((s) => s.language !== hymn.language);
  const switchLang = useCallback(() => {
    const sib = state.current.hymn?.siblings?.find((s) => s.language !== state.current.hymn.language);
    if (!sib) { toast("No linked version in the other language"); return; }
    setPrefs((p) => ({ ...p, language: sib.language }));
    load(sib.id, true);
  }, [load]);

  useEffect(() => { if (index >= slides.length && slides.length) setIndex(slides.length - 1); }, [slides.length, index]);

  const actions = useRef({});
  actions.current = { next: () => step(1), prev: () => step(-1), blank: () => setBlank((b) => !b), first: () => go(0), last: () => go(state.current.slides.length - 1) };

  useEffect(() => {
    const c = new BroadcastChannel(CHANNEL);
    chan.current = c;
    c.onmessage = (e) => {
      if (e.data?.type === "hello") { setDisplayOpen(true); c.postMessage({ type: "state", payload: payloadRef.current() }); }
      if (e.data?.type === "key") actions.current[e.data.action]?.();
    };
    return () => { c.postMessage({ type: "closed" }); c.close(); if (chan.current === c) chan.current = null; };
  }, []);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (NEXT.includes(e.key)) { e.preventDefault(); step(1); }
      else if (PREV.includes(e.key)) { e.preventDefault(); step(-1); }
      else if (e.key === "b" || e.key === "B" || e.key === ".") setBlank((b) => !b);
      else if (e.key === "Home") go(0);
      else if (e.key === "End") go(state.current.slides.length - 1);
      else if (e.key === "l" || e.key === "L") switchLang();
      else if (/^[1-9]$/.test(e.key)) go(Number(e.key) - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, go, switchLang]);

  const onPrefs = (p) => { setPrefs(p); api.put("/projection/settings", { denomination_id: p.denomination_id || null, edition_id: p.edition_id || "", language: p.language }).catch(() => {}); };
  const openDisplay = () => {
    const w = window.open("/projection/display", "hoh-projection-display", "popup=yes,width=1280,height=720");
    if (!w) toast.error("Allow pop-ups for this site to open the audience display");
    else toast("Drag the display window to your projector or TV, then double-click it for full screen.");
  };
  const fullscreen = () => fsRef.current?.requestFullscreen?.().catch(() => toast.error("Full screen isn't available in this browser"));

  return (
    <div className="grid lg:grid-cols-[340px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)_280px] gap-6" data-testid="projection-console">
      <aside className="luxury-card rounded-xl p-4 h-fit space-y-4">
        <div className="space-y-2" data-testid="console-panel-tabs">
          {[[["search", "Hymns"], ["bible", "Bible"], ["ann", "Announcements"]], [["songs", "Special Numbers"], ["programme", "Service Programme"]]].map((row, r) => (
          <div key={r} className={`grid gap-2 ${r ? "grid-cols-2" : "grid-cols-[1fr_1fr_1.5fr]"}`}>{row.map(([k, l]) => (
            <button key={k} onClick={() => setPanel(k)} data-testid={`console-panel-${k}`} aria-pressed={panel === k}
              className={`min-h-[44px] px-1 py-2 rounded-md border text-[10.5px] sm:text-[11px] font-semibold tracking-normal uppercase leading-tight text-center transition-colors ${panel === k ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/15 bg-white/[0.03] text-neutral-300 hover:text-white hover:border-[#D4AF37]/60"}`}>{l}</button>
          ))}</div>))}
        </div>
        <div className={panel === "search" ? "" : "hidden"}><HymnSearch meta={meta} prefs={prefs} onPrefs={onPrefs} onPick={(id) => load(id)} activeId={hymn?.id} /></div>
        <div className={panel === "bible" ? "" : "hidden"}><BiblePanel onPresent={present} /></div>
        {panel === "ann" && <AnnouncementsPanel onPresent={present} canEdit={me.can_edit} />}
        {panel === "songs" && <SongsPanel onPresent={present} canEdit={me.can_edit} />}
        <div className={panel === "programme" ? "" : "hidden"}><ProgrammePanel current={currentDesc} canEdit={me.can_edit} onOpen={openItem} /></div>
      </aside>
      <section className="space-y-4 min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500" data-testid="console-hymn-number">{custom ? custom.label : hymn ? `${numberLabel(hymn, denom)} · ${LANG_LABEL[hymn.language]}` : "Nothing selected"}</div>
            <div className="font-serif-display text-2xl sm:text-3xl text-white truncate" data-testid="console-hymn-title">{custom?.title || hymn?.title || "Choose a hymn, Bible passage, announcement or special number"}</div>
          </div>
          {sibling && <button onClick={switchLang} data-testid="console-switch-lang" className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Languages size={14} /> Switch to {LANG_LABEL[sibling.language]} (L)</button>}
        </div>
        <div ref={fsRef} className="proj-fs relative rounded-lg overflow-hidden ring-1 ring-white/10" onDoubleClick={() => document.fullscreenElement && document.exitFullscreen()}>
          <Stage slide={slide} meta={stageMeta} settings={settings} blank={blank} testId="console-live" />
          {loadingHymn && <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Loader2 className="animate-spin text-[#D4AF37]" /></div>}
          <span className="absolute top-2 left-2 text-[9px] tracking-widest uppercase px-2 py-0.5 rounded bg-red-600/90 text-white">{blank ? "Blank" : "Live"}</span>
        </div>
        <ConsoleControls settings={settings} set={setSettings} blank={blank} onBlank={() => setBlank((b) => !b)} onPrev={() => step(-1)} onNext={() => step(1)}
          canPrev={index > 0} canNext={index < slides.length - 1} onOpenDisplay={openDisplay} onFullscreen={fullscreen} displayOpen={displayOpen} />
        <p className="text-[11px] text-neutral-500 flex items-start gap-2" data-testid="console-keys"><Keyboard size={13} className="shrink-0 mt-0.5" /> → / Space / Page Down: next · ← / Page Up: previous · 1–9: jump to verse · B: blank · L: switch language · Home/End: first/last. Presentation clickers work in either window.</p>
      </section>
      <aside className="lg:col-span-2 xl:col-span-1">
        <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Verses</div>
        {slides.length ? <VerseRail slides={slides} index={index} onGo={go} /> : <p className="text-xs text-neutral-600">Verses appear here.</p>}
      </aside>
    </div>
  );
}
